import React, { useCallback, useEffect, useMemo } from 'react';
import { Typography, Grid, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import { isEqual, toNumber } from 'lodash';

import { GridActionsCellItem, GridActionsColDef, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { Form } from 'mobx-react-form';
import { observer } from 'mobx-react-lite';

import { divide } from 'shared/utils/numberOperations';
import { formatCurrencyFloat, formatInt, formatPercentageString } from 'shared/utils/numberFormatters';

import { taxesFormHelpers } from './taxesFormConfig';
import { useRootStore } from 'stores/RootStore';
import { Table } from 'components/shared/Table';

interface EqualizedMarketValueProps {
  form: Form;
}

interface EqualizedMarketValueRow {
  id: number | string;
  name?: string;
  item?: string;
  value?: number;
  editable?: boolean;
}

const EqualizedMarketValue: React.FC<EqualizedMarketValueProps> = observer(({ form }) => {
  const { underwritingPageStore } = useRootStore();
  const grossBuildingArea = underwritingPageStore.underwritingData?.property?.grossBuildingArea;
  const equalilzationRatioField = form.$('incomeApproach.taxes.projected.equalizationRatio');
  const estimatedMarketValueField = form.$('incomeApproach.taxes.projected.estimatedMarketValue');
  const impliedTaxableAssessedValueField = form.$('incomeApproach.taxes.projected.taxableAssessedValue');
  const taxRateField = form.$('incomeApproach.taxes.taxRate');
  const additionalTaxRatesField = form.$('incomeApproach.taxes.projected.additionalTaxRates');
  const taxLiabilityField = form.$('incomeApproach.taxes.projected.taxLiability');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const additionalTaxRates: EqualizedMarketValueRow[] = additionalTaxRatesField.value || [];

  const taxRate = taxRateField.value || 0;
  const impliedTaxableAssessedValue = impliedTaxableAssessedValueField.value;
  const projectedTaxLiability = taxLiabilityField.value;
  const equalizationRatio = equalilzationRatioField.value;
  const estimatedMarketValue = estimatedMarketValueField.value;

  useEffect(() => {
    taxesFormHelpers.calculateImpliedTaxableAssessedValue(form);
    taxesFormHelpers.calculateEqualizedMarketValueTaxLiability(form);
  }, [estimatedMarketValue, taxRate, equalizationRatio, additionalTaxRates, form]);

  const taxLiabilityColDefs = useMemo(
    (): (GridColDef | GridActionsColDef)[] => [
      {
        field: 'item',
        headerName: 'Item',
        editable: true,
        width: 400,
        sortable: false,
      },
      {
        field: 'value',
        headerName: 'Value',
        valueFormatter: (value, row) => {
          const { name } = row;
          switch (name) {
            case 'taxRate':
            case 'additionalTaxRate':
            case 'equalizationRatio':
              return formatPercentageString(toNumber(value));
            case 'taxLiability':
            case 'impliedTaxableAssessedValue':
            case 'taxLiabilityPSF':
            case 'estimatedMarketValue':
              return formatCurrencyFloat(value);
            case 'grossBuildingArea':
              return formatInt(value);
            default:
              return value;
          }
        },
        editable: true,
        width: 200,
        flex: 1,
        sortable: false,
      },
      {
        field: 'action',
        headerName: 'Action',
        type: 'actions',
        width: 150,
        sortable: false,
        getActions: (params: GridRowParams) => {
          const { row } = params;
          if (row.name !== 'additionalTaxRate') return [];

          return [
            <GridActionsCellItem
              key="delete"
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const id = params.id.toString();
                if (id.startsWith('additional-tax-rate-')) {
                  const index = parseInt(id.replace('additional-tax-rate-', ''));
                  taxesFormHelpers.deleteAdditionalTaxRate(form, index, true);
                }
              }}
            />,
          ];
        },
      },
    ],
    [form]
  );

  const addAdditionalTaxRate = useCallback(() => {
    taxesFormHelpers.addAdditionalTaxRate(form, true);
  }, [form]);

  const processRowUpdate = (newRow: any, oldRow: any) => {
    if (newRow.isTotalRow || isEqual(newRow, oldRow)) {
      return oldRow;
    }
    const { name } = newRow;
    const idStr = newRow.id.toString();
    switch (name) {
      case 'equalizationRatio':
        equalilzationRatioField.set(parseFloat(newRow.value));
        return newRow;
      case 'estimatedMarketValue':
        estimatedMarketValueField.set(parseFloat(newRow.value));
        return newRow;
      case 'additionalTaxRate':
        if (idStr.startsWith('additional-tax-rate-')) {
          const index = parseInt(idStr.replace('additional-tax-rate-', ''));
          const currentAdditionalTaxRates = additionalTaxRatesField.value;
          const itemToUpdate = currentAdditionalTaxRates[index];
          if (itemToUpdate) {
            const id = itemToUpdate.id;
            itemToUpdate.taxRateName = newRow.item;
            itemToUpdate.taxRateValue = parseFloat(newRow.value);
            const updatedItems = currentAdditionalTaxRates.map((item: any) => (item.id === id ? itemToUpdate : item));
            additionalTaxRatesField.set(updatedItems);
          }
        }
        return newRow;
    }
    return oldRow;
  };

  const baseEqualizedMarketValueRows = useMemo(
    (): EqualizedMarketValueRow[] => [
      {
        id: 0,
        item: 'Estimated Market Value',
        name: 'estimatedMarketValue',
        value: estimatedMarketValue,
        editable: true,
      },
      {
        id: 1,
        item: 'Equalization Ratio',
        name: 'equalizationRatio',
        value: equalizationRatio,
        editable: true,
      },
      {
        id: 2,
        item: 'Taxable Assessed Value (Implied)',
        name: 'impliedTaxableAssessedValue',
        value: impliedTaxableAssessedValue,
        editable: false,
      },
      {
        id: 3,
        item: 'Tax Rate',
        name: 'taxRate',
        value: taxRate,
        editable: false,
      },
      {
        id: 'total-liability',
        item: 'Tax Liability (Total)',
        name: 'taxLiability',
        value: projectedTaxLiability,
        editable: false,
      },
      {
        id: 4,
        item: 'Gross Building Area',
        name: 'grossBuildingArea',
        value: grossBuildingArea,
        editable: false,
      },
      {
        id: 'total-liability-psf',
        item: 'Tax Liability (PSF)',
        name: 'taxLiabilityPSF',
        value: divide(projectedTaxLiability, grossBuildingArea),
        editable: false,
      },
    ],
    [
      estimatedMarketValue,
      equalizationRatio,
      impliedTaxableAssessedValue,
      taxRate,
      projectedTaxLiability,
      grossBuildingArea,
    ]
  );

  const taxLiabilityRows = useMemo(() => {
    const combinedRows = [...baseEqualizedMarketValueRows];
    const currentAdditionalTaxRates = additionalTaxRatesField.value;
    currentAdditionalTaxRates.forEach((rate: any, index: number) => {
      const taxRateIndex = combinedRows.findIndex((r) => r.name === 'taxRate');
      combinedRows.splice(taxRateIndex + 1 + index, 0, {
        id: `additional-tax-rate-${index}`,
        item: rate.taxRateName,
        name: 'additionalTaxRate',
        value: rate.taxRateValue,
        editable: true,
      });
    });

    return combinedRows;
  }, [baseEqualizedMarketValueRows, additionalTaxRatesField.value]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={9} container direction="row" justifyContent="space-between">
        <Typography variant="subtitle1">Equalized Market Value</Typography>
        <div>
          <Button sx={{ pr: 3 }} onClick={addAdditionalTaxRate}>
            + Additional Tax Rate
          </Button>
        </div>
      </Grid>
      <Grid
        item
        xs={9}
        sx={{
          minHeight: '150px',
          width: '100%',
          '& .total-row': {
            backgroundColor: '#f5f5f5',
            borderTop: '1px solid',
            fontWeight: 'bold',
            '& .MuiDataGrid-cell': {
              fontWeight: 'bold',
            },
          },
        }}
      >
        <Table
          rows={taxLiabilityRows}
          columns={taxLiabilityColDefs}
          processRowUpdate={processRowUpdate}
          getRowClassName={(params) =>
            params.id === 'total-liability' || params.id === 'total-liability-psf' ? 'total-row' : ''
          }
          isCellEditable={(params) => {
            const { row } = params;
            return row?.editable ?? false;
          }}
          getCellClassName={(params) => {
            const { row } = params;
            if (!row.editable) {
              return 'disabled-cell';
            }
            return '';
          }}
        />
      </Grid>
    </Grid>
  );
});

export { EqualizedMarketValue };
